export const resourcePages = [
  {
    slug: 'nabl-guidelines',
    title: 'NABL Guidelines for Collection Partners | Homvisit',
    h1: 'NABL Guidelines — What Collection Partners Should Know',
    description: 'Practical NABL-oriented guidance for sample collection partners in Kerala — identity, integrity, and documentation upstream of the lab. Homvisit serves labs…',
    intro:
      'NABL accreditation applies to laboratories. Collection partners still influence whether samples arrive fit for purpose. This page summarises what Homvisit aligns to when supporting NABL labs.',
    bullets: [
      'Unambiguous patient / employee identification before draw',
      'Correct container, volume, and order-of-draw discipline',
      'Immediate labelling that matches requisition data',
      'Condition of sample on receipt (haemolysis, clotting, leakage)',
      'Traceable handoff between field and lab intake',
    ],
    paragraphs: [
      'Homvisit does not claim NABL accreditation as a lab. We design collection workflows that reduce pre-analytical errors that NABL labs care about.',
      'If you are a lab evaluating a collection partner, ask about redraw rates, labelling error rates, and how exceptions are communicated.',
    ],
    related: ['/services/diagnostic-lab-partner', '/resources/sample-collection-sop'],
  },
  {
    slug: 'corporate-wellness-guide',
    title: 'Corporate Wellness Guide | Homvisit Kerala Resource',
    h1: 'Corporate Wellness Guide',
    description: 'A practical corporate wellness guide for Kerala employers — where health camps and onsite checkups fit in a wider wellness program. Homvisit serves labs…',
    intro:
      'Wellness is more than a once-a-year camp — but camps remain one of the highest-participation interventions when executed well.',
    bullets: [
      'Define goals: screening, awareness, or insurance compliance',
      'Pick packages with your diagnostic partner',
      'Communicate fasting rules early',
      'Plan shifts so production doesn’t stall',
      'Close the loop with anonymised insights where appropriate',
    ],
    paragraphs: [
      'Homvisit supports the collection layer of corporate wellness — phlebotomy teams that make onsite screening operationally realistic.',
    ],
    related: ['/services/corporate-health-checkups', '/resources/corporate-health-screening-guide'],
  },
  {
    slug: 'health-camp-checklist',
    title: 'Corporate Health Camp Checklist Guide | Homvisit Kerala',
    h1: 'Health Camp Checklist',
    description: 'Corporate / medical health camp checklist — venue, staffing, consumables, consent, and sample dispatch. Homvisit serves labs, hospitals, and HR teams across…',
    intro: 'Use this checklist before your next corporate or community medical camp in Kerala.',
    bullets: [
      'Confirmed headcount and shift windows',
      'Venue with power, seating, privacy screens',
      'Phlebotomist count vs expected throughput',
      'Consumables buffer (tubes, needles, alcohol swabs, gloves)',
      'ID / emp-code process agreed with HR',
      'Biohazard waste plan',
      'Sample dispatch / pickup time locked',
      'Emergency contact on site',
    ],
    paragraphs: [
      'Homvisit can own phlebotomy staffing and collection flow while your HR owns communication and attendance.',
    ],
    related: [
      '/services/corporate-health-camps',
      '/how-it-works/corporate-medical-camps',
      '/insights/corporate-health-camps-planning-checklist',
    ],
  },
  {
    slug: 'sample-collection-sop',
    title: 'Sample Collection SOP Overview Guide | Homvisit Kerala',
    h1: 'Sample Collection SOP',
    description: 'High-level sample collection SOP used as Homvisit’s operating baseline — customise per diagnostic partner. Homvisit serves labs, hospitals, and HR teams…',
    intro:
      'Every lab has nuances. Below is the baseline SOP mindset Homvisit uses before partner-specific customisation.',
    bullets: [
      'Verify identity against requisition',
      'Confirm fasting / special conditions when relevant',
      'Hand hygiene + gloves',
      'Correct venipuncture technique and order of draw',
      'Invert tubes as required; never relabel casually',
      'Segregate sharps immediately',
      'Log unable-to-collect cases with reason',
    ],
    paragraphs: [
      'Partners receive a tailored SOP addendum covering tube lists, barcodes, and rejection definitions.',
    ],
    related: ['/services/sample-collection-services', '/how-it-works/quality-assurance'],
  },
  {
    slug: 'corporate-health-screening-guide',
    title: 'Corporate Health Screening Guide | Homvisit Kerala',
    h1: 'Corporate Health Screening Guide',
    description: 'How to run corporate health screening in Kerala — formats, communication, and collection logistics. Homvisit serves labs, hospitals, and HR teams across Kerala.',
    intro:
      'Screening programs succeed when logistics respect employee time. Onsite collection beats asking hundreds of people to visit a lab individually.',
    bullets: [
      'Choose annual vs targeted screening',
      'Align test menu with budget and risk profile',
      'Decide camp vs staggered home collection',
      'Brief employees in Malayalam + English as needed',
      'Protect privacy of results through your clinical partner',
    ],
    paragraphs: [
      'Homvisit’s role is operational: get high-quality samples collected efficiently so your screening partner can report.',
    ],
    related: ['/services/corporate-health-checkups', '/resources/pre-test-instructions'],
  },
  {
    slug: 'pre-test-instructions',
    title: 'Pre-test Instructions for Camps Guide | Homvisit Kerala',
    h1: 'Pre-test Instructions',
    description: 'Common pre-test instructions for blood collection — fasting, hydration, and medication notes employees should know before a camp. Homvisit serves labs…',
    intro:
      'Always follow the ordering clinician or lab. These are common patterns Homvisit sees at corporate camps.',
    bullets: [
      'Fasting glucose / lipid panels: typically 8–12 hours fasting (water usually allowed)',
      'Stay hydrated unless told otherwise',
      'Bring employee ID / unique code',
      'Mention anticoagulant or special conditions to the phlebotomist',
      'Avoid strenuous exercise immediately before certain tests if advised',
    ],
    paragraphs: [
      'HR should circulate lab-approved instructions at least 48 hours before the camp.',
    ],
    related: ['/resources/health-camp-checklist', '/services/corporate-health-camps'],
  },
  {
    slug: 'faqs',
    title: 'Homvisit FAQs on Camps, Labs & Phlebotomy in Kerala',
    h1: 'Frequently Asked Questions',
    description: 'FAQs about Homvisit corporate camps, onsite phlebotomy partnerships, home collection, and joining the phlebotomist network across Kerala. Homvisit serves…',
    intro: 'Quick answers for corporate buyers, lab partners, and phlebotomists.',
    bullets: [
      'Primary focus: B2B camps, onsite phlebotomy, lab partnerships',
      'Home collection: available, secondary to B2B',
      'Coverage: Kerala statewide with district pages for local detail',
      'Phlebotomists: always-open Join Network at /jobs',
      'Contact: 75 9409 9409 · sujithvijayan@homvisit.com',
    ],
    paragraphs: [
      'For service-specific FAQs, open the relevant /services/ page — each commercial page includes targeted questions.',
    ],
    faqs: [
      {
        q: 'Does Homvisit only do home blood collection?',
        a: 'No. Primary work is corporate health camps and onsite phlebotomy for labs and employers. Home collection is available as a secondary service.',
      },
      {
        q: 'Which districts do you cover?',
        a: 'Statewide Kerala. See district pages under /services for local detail.',
      },
      {
        q: 'How do phlebotomists join?',
        a: 'Apply once at /jobs. The network stays open; we call when work matches your area.',
      },
    ],
    related: [
      '/services/phlebotomy-services-kerala',
      '/jobs',
      '/resources/health-camp-checklist',
    ],
  },
]

export function getResourcePage(slug) {
  return resourcePages.find((p) => p.slug === slug) || null
}
